import { hasReplied as hasRepliedAtom, replyCount } from '@/atoms/stats'
import { canWriteAtom } from '@/atoms/session'
import { HugeiconsIcon } from '@hugeicons/react'
import { Comment } from '@hugeicons/core-free-icons'
import { Event } from '@nostr/tools/wasm'
import * as kinds from '@nostr/tools/kinds'
import { useAtomValue } from 'jotai'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import PostEditor from '../PostEditor'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { useSecondaryPage } from '@/PageManager'
import { toNote } from '@/lib/link'
import { formatCount } from './utils'

export default function ReplyButton({ event }: { event: Event }) {
  const { t } = useTranslation()
  const { push } = useSecondaryPage()
  const count = useAtomValue(replyCount(event.id))
  const hasReplied = useAtomValue(hasRepliedAtom(event.id))

  const isForumTopicComment =
    event.kind === kinds.Comment &&
    event.tags.some(([name, value]) => name === 'K' && value === String(kinds.ForumThread))
  const isForumThread =
    event.kind === kinds.ForumThread && !!event.tags.find(([name]) => name === 'h')
  const isForumTopicReply = isForumTopicComment || isForumThread
  const threadRootId = isForumTopicComment
    ? event.tags.find(([name]) => name === 'E')?.[1]
    : isForumThread
      ? event.id
      : undefined

  const [noticeOpen, setNoticeOpen] = useState(false)
  const [open, setOpen] = useState(false)
  const canWrite = useAtomValue(canWriteAtom)

  function handleReplyClick(e: React.MouseEvent) {
    e.stopPropagation()
    if (isForumTopicReply) {
      setNoticeOpen(true)
      return
    }
    setOpen(true)
  }

  return (
    <>
      <button
        className="flex gap-1 items-center enabled:hover:text-blue-400 disabled:opacity-50 disabled:cursor-not-allowed pr-3 h-full text-muted-foreground"
        onClick={handleReplyClick}
        disabled={!canWrite}
        title={t('Reply')}
      >
        <HugeiconsIcon icon={Comment} className={`${hasReplied ? 'text-primary' : ''}`} />
        {!!count && (
          <div className={`text-sm ${hasReplied ? 'text-primary' : ''}`}>{formatCount(count)}</div>
        )}
      </button>
      <PostEditor parentEvents={[event]} open={open} setOpen={setOpen} />
      <Dialog open={noticeOpen} onOpenChange={setNoticeOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {isForumTopicComment
                ? t('This is a comment on a forum topic')
                : t('This is a forum topic')}
            </DialogTitle>
            <DialogDescription>
              {t(
                'Commenting here will not make your note show up in the original thread. To reply to the original thread, visit the thread directly.'
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            {isForumTopicComment && threadRootId && (
              <Button
                onClick={() => {
                  setNoticeOpen(false)
                  push(toNote(threadRootId))
                }}
              >
                {t('Visit thread')}
              </Button>
            )}
            {isForumThread && (
              <Button
                onClick={() => {
                  setNoticeOpen(false)
                  window.dispatchEvent(new CustomEvent('scroll-to-forum-reply-box'))
                }}
              >
                {t('Reply to thread')}
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => {
                setNoticeOpen(false)
                setOpen(true)
              }}
            >
              {t('Comment anyway')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
